import { ESTATE_ROLE_OPTIONS } from "@/shared/constants/contactDetailOptions";

/**
 * Generates initials from a person's name
 * @param options - Either a full name string or separate first/last names
 * @param options.fullName - Full name string (e.g., "John Doe")
 * @param options.firstName - First name string
 * @param options.lastName - Last name string
 * @param maxLength - Maximum number of initials to return (default: 2)
 * @returns Uppercase initials string (e.g., "JD" for "John Doe")
 */
export function getInitials(
  options:
    | { fullName: string; firstName?: never; lastName?: never }
    | { fullName?: never; firstName: string; lastName: string },
  maxLength = 2,
): string {
  let initials: string;

  if (options.fullName) {
    // Handle full name approach: split by spaces and take first char of each
    initials = options.fullName
      .split(" ")
      .map((name) => name.charAt(0))
      .join("");
  } else {
    // Handle separate first/last name approach
    const firstName = options.firstName || "";
    const lastName = options.lastName || "";
    initials = `${firstName.charAt(0)}${lastName.charAt(0)}`;
  }

  return initials.toUpperCase().slice(0, maxLength) || "U";
}

export function getFullName(firstName: string | null, lastName: string | null): string {
  return [firstName, lastName].filter(Boolean).join(" ");
}

export function getDisplayName(options: {
  fullName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  fallback?: string;
}): string {
  const { fullName, firstName, lastName, email, fallback = "User" } = options;
  return (
    fullName || getFullName(firstName ?? null, lastName ?? null) || email?.split("@")[0] || fallback
  );
}

type RoundingOption = "none" | "whole" | "thousand" | "none-decimal";

export const AccountType = {
  Investment: "investment",
  Credit: "credit",
  Depository: "depository",
  Loan: "loan",
  Brokerage: "brokerage",
  Other: "other",
} as const;

export type AccountTypeValue = (typeof AccountType)[keyof typeof AccountType];

export function formatDollars({
  amount,
  currency = "USD",
  type = "depository",
  rounding = "whole",
}: {
  amount: number;
  currency?: string;
  type?: AccountTypeValue | string | null;
  rounding?: RoundingOption;
}) {
  let formattedAmount: string;
  const isNegative = type === AccountType.Credit || type === AccountType.Loan || amount < 0;
  const absAmount = Math.abs(amount);

  switch (rounding) {
    case "whole":
      formattedAmount = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(absAmount);
      break;
    case "thousand":
      formattedAmount = `${Math.trunc(absAmount / 1000)}k`;
      break;
    case "none-decimal":
      formattedAmount = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
      })
        .format(absAmount)
        .replace(/\.00/, "");
      break;
    case "none":
    default:
      formattedAmount = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(absAmount);
      break;
  }

  return `${isNegative ? "-" : ""}${formattedAmount}`;
}

export function formatTransactionAmount(amount: number, isInflow: boolean): string {
  const formatted = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(amount));
  return `${isInflow ? "+" : "-"}$${formatted}`;
}

export function formatCompactDollars(amount: number): string {
  const abs = Math.abs(amount);
  const sign = amount < 0 ? "-" : "";
  if (abs === 0) return "$0";
  if (abs < 1_000) return `${sign}$${Math.round(abs)}`;

  let value: number;
  let suffix: string;
  if (abs < 1_000_000) {
    value = abs / 1_000;
    suffix = "K";
  } else if (abs < 1_000_000_000) {
    value = abs / 1_000_000;
    suffix = "M";
  } else {
    value = abs / 1_000_000_000;
    suffix = "B";
  }

  const rounded = Math.round(value * 10) / 10;
  if (suffix === "K" && rounded >= 1000) return `${sign}$${(rounded / 1000).toFixed(1)}M`;
  if (suffix === "M" && rounded >= 1000) return `${sign}$${(rounded / 1000).toFixed(1)}B`;
  const formatted = rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1);
  return `${sign}$${formatted}${suffix}`;
}

export function formatAccountType(type: AccountTypeValue | string | null | undefined) {
  switch (type) {
    case AccountType.Credit:
      return "Credit";
    case AccountType.Loan:
      return "Loans";
    case AccountType.Depository:
      return "Cash";
    case AccountType.Investment:
      return "Investments";
    case AccountType.Brokerage:
      return "Brokerage";
    default:
      return "Other";
  }
}

/**
 * ========================================================================
 * DATE UTILITIES - Timezone-safe date handling for date_of_birth/death
 * ========================================================================
 *
 * These utilities ensure dates from the database (stored as DATE type, not TIMESTAMP)
 * are displayed correctly regardless of user timezone.
 *
 * CRITICAL: Always use these functions for date_of_birth and date_of_death fields.
 * Never use `new Date(dateString)` directly as it interprets dates as UTC midnight,
 * causing off-by-one-day errors for users in timezones behind UTC.
 */

/**
 * Parse a date string from the database as a local Date object.
 * This prevents timezone-related off-by-one-day bugs.
 *
 * @param dateString - Date string in YYYY-MM-DD format (e.g., "2024-12-04")
 * @returns Date object set to noon local time, or undefined if invalid
 *
 * @example
 * parsePersonDateAsLocal("2024-12-04") // December 4, 2024 at 12:00:00 local time
 */
export function parsePersonDateAsLocal(dateString: string | null | undefined): Date | undefined {
  if (!dateString) return undefined;

  // Extract date components using regex to handle both "YYYY-MM-DD" and ISO formats
  const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return undefined;

  const [, year, month, day] = match;
  // Create date at noon local time to avoid any DST boundary issues
  return new Date(Number(year), Number(month) - 1, Number(day), 12, 0, 0, 0);
}

/**
 * Format a date string from the database for display.
 * Uses parsePersonDateAsLocal to avoid timezone bugs.
 *
 * @param dateString - Date string in YYYY-MM-DD format
 * @param options - Intl.DateTimeFormatOptions for formatting (default: short month, numeric day/year)
 * @returns Formatted date string (e.g., "Dec 4, 2024") or fallback text
 *
 * @example
 * formatPersonDateForDisplay("2024-12-04") // "Dec 4, 2024"
 * formatPersonDateForDisplay("2024-12-04", { month: 'long' }) // "December 4, 2024"
 * formatPersonDateForDisplay(null, {}, "N/A") // "N/A"
 */
export function formatPersonDateForDisplay(
  dateString: string | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
  },
  fallback: string = "—",
): string {
  const date = parsePersonDateAsLocal(dateString);
  if (!date) return fallback;

  return date.toLocaleDateString("en-US", options);
}

/**
 * Format a Date object for saving to the database.
 * Returns YYYY-MM-DD string WITHOUT timezone conversion.
 *
 * @param date - Date object to format
 * @returns Date string in YYYY-MM-DD format, or null if date is undefined
 *
 * @example
 * const date = new Date(2024, 11, 4); // December 4, 2024
 * formatPersonDateForSave(date) // "2024-12-04"
 */
export function formatPersonDateForSave(date: Date | undefined | null): string | null {
  if (!date) return null;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

const ISO_DATE_PREFIX_PATTERN = /^(\d{4})-(\d{2})-(\d{2})/;
const US_DATE_PATTERN = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
const WRITTEN_DATE_PATTERN = /^([A-Za-z]+)\.? (\d{1,2}),? (\d{4})$/;
const MONTH_PREFIXES = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
] as const;

const monthFromName = (name: string): number =>
  MONTH_PREFIXES.findIndex((prefix) => name.toLowerCase().startsWith(prefix)) + 1;

/**
 * Normalize a full date ("2026-02-17", "02/17/2026" or "February 17, 2026") to YYYY-MM-DD.
 * Partial dates such as "2026-02" or "February 2026" return an empty string.
 */
export function normalizeFullDate(value: string | null | undefined): string {
  const trimmed = value?.trim() ?? "";
  const isoMatch = trimmed.match(ISO_DATE_PREFIX_PATTERN);
  const usMatch = trimmed.match(US_DATE_PATTERN);
  const writtenMatch = trimmed.match(WRITTEN_DATE_PATTERN);

  const parts = isoMatch
    ? { year: Number(isoMatch[1]), month: Number(isoMatch[2]), day: Number(isoMatch[3]) }
    : usMatch
      ? { year: Number(usMatch[3]), month: Number(usMatch[1]), day: Number(usMatch[2]) }
      : writtenMatch
        ? {
            year: Number(writtenMatch[3]),
            month: monthFromName(writtenMatch[1]),
            day: Number(writtenMatch[2]),
          }
        : null;

  const date =
    parts && parts.month > 0 ? new Date(parts.year, parts.month - 1, parts.day, 12) : undefined;

  const isValid =
    !!parts &&
    !!date &&
    date.getFullYear() === parts.year &&
    date.getMonth() === parts.month - 1 &&
    date.getDate() === parts.day;

  return isValid ? (formatPersonDateForSave(date) ?? "") : "";
}

/**
 * Legacy formatDate function - DEPRECATED
 * Use formatPersonDateForDisplay instead for date_of_birth/date_of_death
 * This is kept for backwards compatibility with non-person date fields
 */
export function formatDate(dateString: string) {
  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
  };
  return date.toLocaleDateString("en-US", options);
}

export function formatTime(dateString: string) {
  const date = new Date(dateString);
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
}

export function formatRelativeTime(dateString: string | null | undefined): string {
  if (!dateString) return "";
  const diffMs = Date.now() - new Date(dateString).getTime();
  if (diffMs < 0) return "just now";
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  const years = Math.floor(days / 365);
  return `${years}yr ago`;
}

export function formatZipCode(zipCode: number | null | undefined): string {
  if (zipCode === null || zipCode === undefined || zipCode === 0) return "";
  return zipCode.toString().padStart(5, "0");
}

export function formatLegalRole(legalRole: string[] | null): string {
  if (!legalRole || legalRole.length === 0) return "—";

  const legacyLabels: Record<string, string> = {
    successor_executor: "Successor Executor",
    successor_trustee: "Successor Trustee",
    personal_representative: "Personal Representative",
    power_of_attorney: "Power of Attorney",
    healthcare_proxy: "Healthcare Proxy",
    financial_advisor: "Financial Advisor",
    co_executor_co_trustee: "Co-Executor / Co-Trustee",
  };

  return legalRole
    .map((role) => {
      const fromOptions = ESTATE_ROLE_OPTIONS.find((o) => o.value === role);
      if (fromOptions) return fromOptions.label;
      if (legacyLabels[role]) return legacyLabels[role];
      return role.replace(/[-_]/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
    })
    .join(", ");
}

interface FormattableAddress {
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: number | string | null;
}

export function formatAddress(address: FormattableAddress | null): string {
  if (!address) return "";

  // Helper to check if a value is meaningfully present
  const isPresent = (value: string | number | null | undefined): boolean => {
    if (value === null || value === undefined) return false;
    if (typeof value === "string" && (!value.trim() || value === ".")) return false;
    if (typeof value === "number" && value === 0) return false;
    return true;
  };

  // Clean up the address components
  const components = {
    street: [address.addressLine1, address.addressLine2].filter(isPresent).join(" "),
    cityState: [
      address.city,
      address.state,
      // Only include zipCode if it's a meaningful value
      isPresent(address.zipCode) &&
      (typeof address.zipCode === "number" ? address.zipCode > -1 : true)
        ? formatZipCode(
            typeof address.zipCode === "number"
              ? address.zipCode
              : parseInt(address.zipCode || "0", 10),
          )
        : null,
    ]
      .filter(isPresent)
      .join(", "),
  };

  // Build the final address string
  return [components.street, components.cityState].filter(Boolean).join(", ");
}

/**
 * Formats a phone number string to display format (123) 456-7890
 * @param phone - Phone number string (digits only or mixed format)
 * @returns Formatted phone number string or em dash if invalid/empty
 */
export function cleanSSN(value: string): string {
  return value.replace(/\D/g, "");
}

export function formatSSN(ssn: string): string {
  const digits = cleanSSN(ssn);
  if (digits.length !== 9) return ssn;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}

export function isValidSSN(value: string): boolean {
  return cleanSSN(value).length === 9;
}

export function stripPhone(value: string): string {
  return value.replace(/\D/g, "");
}

export function formatPhoneNumber(phone: string): string {
  if (!phone) return "—";
  const digitsOnly = phone.replace(/\D/g, "");
  if (digitsOnly.length === 10) {
    return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`;
  }
  return phone; // Return original if not 10 digits
}

export function formatPhoneInput(value: string): string {
  const numbers = value.replace(/\D/g, "");
  const limited = numbers.slice(0, 10);

  if (limited.length === 0) return "";
  if (limited.length <= 3) return `(${limited}`;
  if (limited.length <= 6) return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
  return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
}

export function formatHoursLabel(totalMinutes: number): string {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours === 0) return `${minutes} min`;
  if (minutes === 0) return hours === 1 ? "1 hour" : `${hours} hours`;

  const hourPart = hours === 1 ? "1 hour" : `${hours} hours`;
  return `${hourPart} ${minutes} min`;
}

export function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
}
